import { useState, useEffect } from 'react';
import { Container } from '../components/Container';
import { PageHeader } from '../components/PageHeader';
import { ErrorAlert } from '../components/ErrorAlert';
import { useKeyManagement } from '../hooks/useKeyManagement';
import { useErrorHandling } from '../hooks/useErrorHandling';
import { useNavigate, useSearchParams } from 'react-router-dom';

enum CliAuthState {
  PENDING = 'PENDING',
  LOADING = 'LOADING',
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR',
}

export default function CliAuthPage() {
  const { createKey } = useKeyManagement();
  const { error, handleError, clearError } = useErrorHandling();
  const [state, setState] = useState<CliAuthState>(CliAuthState.PENDING);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const navigate = useNavigate();

  // get query params
  const [searchParams] = useSearchParams();
  const callback = searchParams.get('callback');
  const api_key_name = searchParams.get('api_key_name');

  // Check parameters on mount
  useEffect(() => {
    if (!callback || !api_key_name) {
      setState(CliAuthState.ERROR);
      setErrorMsg('Missing callback URL or API key name');
    }
  }, [callback, api_key_name]);

  // Handle key creation
  const handleCreateKey = async () => {
    if (!callback || !api_key_name) {
      setState(CliAuthState.ERROR);
      setErrorMsg('Missing callback URL or API key name');
      return;
    }

    setState(CliAuthState.LOADING);

    try {
      const formData = {
        name: api_key_name,
        expires_at: null,
      };
      const createdKey = await createKey(formData);

      // Build the callback URL with parameters
      const params = new URLSearchParams();
      Object.entries(createdKey).forEach(([key, value]) => {
        params.append(key, value as string);
      });

      // Use redirect instead of fetch to avoid CORS issues
      window.location.href = `${callback}?${params.toString()}`;

      // Note: No need to set success state as we're redirecting away
    } catch (err) {
      setState(CliAuthState.ERROR);
      setErrorMsg(err instanceof Error ? err.message : 'An unknown error occurred');
      handleError(err);
    }
  };

  const handleCancel = () => {
    navigate('/api-keys');
  };

  return (
    <Container>
      <PageHeader title="CLI Authentication" description="Authenticate Jua CLI" />

      <div className="bg-white shadow sm:rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          {state === CliAuthState.PENDING && (
            <div>
              <h3 className="text-lg font-medium leading-6 text-gray-900">
                CLI Authentication Request
              </h3>
              <div className="mt-2 max-w-xl text-sm text-gray-500">
                <p>
                  The Jua CLI is requesting to create an API key named "{api_key_name}". Do you want
                  to authorize this request?
                </p>
              </div>
              <div className="mt-5 flex space-x-4">
                <button
                  type="button"
                  onClick={handleCreateKey}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Authorize
                </button>
                <button
                  type="button"
                  onClick={handleCancel}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {state === CliAuthState.LOADING && (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-4 text-gray-600">Authenticating CLI...</p>
            </div>
          )}

          {state === CliAuthState.SUCCESS && (
            <div className="text-center py-8">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                <svg
                  className="h-6 w-6 text-green-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <h3 className="mt-4 text-lg font-medium text-gray-900">Authentication Successful</h3>
              <p className="mt-2 text-sm text-gray-500">
                The CLI has been successfully authenticated. You can now close this window and
                return to the CLI.
              </p>
              <div className="mt-5">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Return to Keys
                </button>
              </div>
            </div>
          )}

          {state === CliAuthState.ERROR && (
            <div className="py-4">
              <ErrorAlert error={errorMsg || (error ? error.message : null)} onClose={clearError} />
              <div className="mt-5 text-center">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Return to Keys
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Container>
  );
}
